import type { EstimateVsActualReport } from '../../services/analytics.api';
import { cn } from '../../utils/cn';
import { Panel, StatTile, tbl } from '../ui';

const baghdadTime = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    timeZone: 'Asia/Baghdad',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

export default function EstimateVsActual({ eva }: { eva?: EstimateVsActualReport }) {
  return (
    <Panel title="Estimate vs actual" subtitle="Planned ETAs and service vs what really happened">
      {(eva?.coverage.stops_matched ?? 0) === 0 ? (
        <p className="text-[13px] text-gray-500 text-pretty">
          No completed planned stops in this window yet ({eva?.coverage.stops_planned ?? 0} stops planned).
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <StatTile
              variant="inset"
              label="ETA error (median)"
              value={eva?.arrival.abs_p50_min ?? 0}
              unit="min"
              sub={`p90 ${eva?.arrival.abs_p90_min ?? 0} min`}
            />
            <StatTile
              variant="inset"
              label="Arrivals within 30 min"
              value={eva?.arrival.within_30_pct ?? 0}
              unit="%"
              sub={`within 15: ${eva?.arrival.within_15_pct ?? 0}% · within 60: ${eva?.arrival.within_60_pct ?? 0}%`}
            />
            <StatTile
              variant="inset"
              label="ETA bias"
              value={eva?.arrival.bias_min ?? 0}
              unit="min"
              sub={(eva?.arrival.bias_min ?? 0) > 0 ? 'drivers arrive later than plan' : 'drivers arrive earlier than plan'}
            />
            <StatTile
              variant="inset"
              label="Service: plan vs actual"
              value={`${eva?.service.planned_avg_min ?? 0} → ${eva?.service.actual_avg_min ?? 0}`}
              unit="min"
              sub={`error p50 ${eva?.service.err_p50_min ?? 0} min`}
            />
            <StatTile
              variant="inset"
              label="Task finish error"
              value={eva?.task_finish.abs_p50_min ?? 0}
              unit="min"
              sub={`within 30 min: ${eva?.task_finish.within_30_pct ?? 0}%`}
            />
            <StatTile
              variant="inset"
              label="Routes overrun >30 min"
              value={eva?.route_finish.overrun_30_pct ?? 0}
              unit="%"
              sub={`${eva?.route_finish.routes ?? 0} routes · finish err p50 ${eva?.route_finish.err_p50_min ?? 0} min`}
            />
          </div>
          <p className="mt-3 text-[12px] text-gray-500 text-pretty">
            Coverage: {eva?.coverage.stops_matched ?? 0} of {eva?.coverage.stops_planned ?? 0} planned stops
            matched to core actuals ({eva?.coverage.match_rate_pct ?? 0}%).
            {eva?.verso_arrival &&
              ` Solver-only ETA (verso) error p50: ${eva.verso_arrival.abs_p50_min} min.`}
          </p>
          {(eva?.by_driver.length ?? 0) > 0 && (
            <div className={cn(tbl.wrap, 'mt-4')}>
              <table className={tbl.table}>
                <thead>
                  <tr className={tbl.headRow}>
                    <th className={tbl.th}>Driver</th>
                    <th className={cn(tbl.th, tbl.num)}>Stops</th>
                    <th className={cn(tbl.th, tbl.num)}>|ETA err| p50</th>
                    <th className={cn(tbl.th, tbl.num)}>Bias</th>
                  </tr>
                </thead>
                <tbody>
                  {eva?.by_driver.slice(0, 8).map((d) => (
                    <tr key={d.driver_uid} className={tbl.row}>
                      <td className={tbl.td}><span dir="auto">{d.driver_name}</span></td>
                      <td className={cn(tbl.td, tbl.num)}>{d.stops}</td>
                      <td className={cn(tbl.td, tbl.num, 'font-medium')}>{d.abs_p50_min} m</td>
                      <td className={cn(tbl.td, tbl.num)}>{d.bias_min > 0 ? '+' : ''}{d.bias_min} m</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(eva?.worst_stops.length ?? 0) > 0 && (
            <>
              <p className="mt-4 text-[12px] text-gray-500">Worst misses</p>
              <ul className="mt-1">
                {eva?.worst_stops.slice(0, 5).map((w) => (
                  <li
                    key={w.task_uid}
                    className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-black/[0.04] py-2 text-[13px] text-gray-500 tabular-nums last:border-0"
                  >
                    <span className="text-gray-900" dir="auto">{w.driver_name}</span>
                    <span>
                      planned {baghdadTime(w.planned_eta)} → arrived {baghdadTime(w.actual_arrived)}
                    </span>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
                        w.err_min > 0 ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {w.err_min > 0 ? '+' : ''}{w.err_min} min
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </Panel>
  );
}
